import React from 'react';
import MemberAvatar from './MemberAvatar';
import type { MemberSpec } from './types';
import { TYPE } from '../../constants/typeScale';

/**
 * WHO is in this crew, as a row-sized overlapping stack of faces.
 *
 * A conversation row used to show one GroupAvatar — a name badge, which says nothing about the people the
 * creator actually recruited. The members already travel with the conversation list (`ChatSession.members`,
 * from `listConversations`), so the faces cost nothing extra: no per-row fetch, which in a list is the
 * classic N+1 trap.
 *
 * Capped at `max` faces plus a "+N" so a full twelve-agent crew still fits a 240px pane.
 */
interface Props {
  members: MemberSpec[];
  /** Needed to resolve Drive-hosted avatars through the media proxy (MemberAvatar handles the rest). */
  conversationId?: string;
  /** How many faces before the overflow badge. Three keeps the stack inside the row's avatar column. */
  max?: number;
  className?: string;
}

const MemberStack: React.FC<Props> = ({ members, conversationId, max = 3, className = '' }) => {
  const shown = members.slice(0, max);
  const extra = members.length - shown.length;
  const names = members.map((m) => m.alias || m.name).join(', ');
  return (
    <span
      className={`flex shrink-0 items-center ${className}`}
      role="img"
      aria-label={`Crew: ${names}`}
      title={names}
    >
      {shown.map((m, i) => (
        <MemberAvatar
          key={m.id}
          name={m.alias || m.name}
          avatarIcon={m.avatar_icon}
          avatarPath={m.avatar_path}
          avatarDriveFileId={m.avatar_drive_file_id}
          conversationId={conversationId}
          /* 24px, not the 32px of the GroupAvatar it replaces: three faces plus a count have to leave the
             caption enough room to say "This production's crew" without truncating in a 17rem pane. */
          className={`h-6 w-6 ring-2 ring-white dark:ring-black ${i ? '-ml-2.5' : ''}`}
        />
      ))}
      {extra > 0 && (
        <span className={`-ml-2.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-secondary-100
                         ${TYPE.micro} font-medium text-secondary-500 ring-2 ring-white
                         dark:bg-secondary-800 dark:text-secondary-300 dark:ring-black`}>
          +{extra}
        </span>
      )}
    </span>
  );
};

export default MemberStack;
